import React, { useState, useEffect, useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { Link } from "react-router-dom";
import apiService from "../../services/api";

// Fallback brands if API fails
const fallbackBrands = [
  {
    name: "Partner 1",
    logo_image: "/assets/img/brand/brand_1_1.svg",
    website_url: "#",
  },
  {
    name: "Partner 2",
    logo_image: "/assets/img/brand/brand_1_2.svg",
    website_url: "#",
  },
  {
    name: "Partner 3",
    logo_image: "/assets/img/brand/brand_1_3.svg",
    website_url: "#",
  },
  {
    name: "Partner 4",
    logo_image: "/assets/img/brand/brand_1_4.svg",
    website_url: "#",
  },
  {
    name: "Partner 5",
    logo_image: "/assets/img/brand/brand_1_5.svg",
    website_url: "#",
  },
  {
    name: "Partner 6",
    logo_image: "/assets/img/brand/brand_1_6.svg",
    website_url: "#",
  },
  {
    name: "Partner 7",
    logo_image: "/assets/img/brand/brand_1_7.svg",
    website_url: "#",
  },
  {
    name: "Partner 8",
    logo_image: "/assets/img/brand/brand_1_8.svg",
    website_url: "#",
  },
];

function BrandOne({className}) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await apiService.getBrands();
        if (response && response.results && response.results.length > 0) {
          setBrands(response.results);
        } else {
          // Use fallback data if no brands from API
          setBrands(fallbackBrands);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        // Use fallback data on error
        setBrands(fallbackBrands);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  // Calculate Swiper configuration based on slide count
  const swiperConfig = useMemo(() => {
    const slideCount = brands.length;
    const minSlidesForLoop = 10; // Minimum slides to enable loop mode
    const shouldEnableLoop = slideCount >= minSlidesForLoop;
    
    // If we have fewer slides than needed for loop, duplicate them
    let slidesToShow = brands;
    if (!shouldEnableLoop && slideCount > 0 && slideCount < minSlidesForLoop) {
      // Duplicate slides to reach minimum for smooth infinite scroll
      const duplicatesNeeded = Math.ceil(minSlidesForLoop / slideCount);
      slidesToShow = Array(duplicatesNeeded).fill(brands).flat();
    }

    console.log(`BrandOne: ${slideCount} slides, loop: ${shouldEnableLoop || slidesToShow.length >= minSlidesForLoop}`);
    
    return {
      slides: slidesToShow,
      loop: shouldEnableLoop || slidesToShow.length >= minSlidesForLoop,
      modules: [Autoplay],
      autoplay: { delay: 3000, disableOnInteraction: false },
      speed: 1000,
      spaceBetween: 20,
      breakpoints: {
        0: { slidesPerView: 1 },
        576: { slidesPerView: 2 },
        768: { slidesPerView: 3 },
        992: { slidesPerView: 3 },
        1200: { slidesPerView: 6 },
        1400: { slidesPerView: 8 },
      }
    };
  }, [brands]);

  if (loading) {
    return (
      <div className={`brand-area overflow-hidden ${className}`}>
        <div className="container th-container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading partners...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (swiperConfig.slides.length === 0) {
    return (
      <div className={`brand-area overflow-hidden ${className}`}>
        <div className="container th-container">
          <div className="text-center p-4">
            <p>No partner brands available at the moment.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`brand-area overflow-hidden ${className}`}>
      <div className="container th-container">
        <Swiper
          modules={swiperConfig.modules}
          autoplay={swiperConfig.autoplay}
          loop={swiperConfig.loop}
          speed={swiperConfig.speed}
          spaceBetween={swiperConfig.spaceBetween}
          breakpoints={swiperConfig.breakpoints}
          className="brandSlider1"
        >
          {swiperConfig.slides.map((brand, index) => {
            // Handle both API structure and fallback structure
            const brandName = brand.name || `Brand ${index + 1}`;
            // Use fallback images from static assets if API brand doesn't have logo_image
            const brandImage = brand.logo_image || 
                              (brand.name ? `/assets/img/brand/brand_1_${(index % 8) + 1}.svg` : brand) ||
                              `/assets/img/brand/brand_1_${(index % 8) + 1}.svg`;
            const brandUrl = brand.website_url || "#";
            const isExternalLink = brandUrl && brandUrl !== "#" && brandUrl.startsWith('http');
            
            // Create unique key for duplicated slides
            const uniqueKey = `${brand.id || brand.name || index}-${Math.floor(index / brands.length)}`;
            
            return (
              <SwiperSlide key={uniqueKey}>
                <div className="brand-box">
                  {isExternalLink ? (
                    <a href={brandUrl} target="_blank" rel="noopener noreferrer" title={brandName}>
                      <img
                        className="original"
                        src={brandImage}
                        alt={`${brandName} Logo`}
                      />
                      <img
                        className="gray"
                        src={brandImage}
                        alt={`${brandName} Logo`}
                      />
                    </a>
                  ) : (
                    <Link to={brandUrl === "#" ? "#" : brandUrl} title={brandName}>
                      <img
                        className="original"
                        src={brandImage}
                        alt={`${brandName} Logo`}
                      />
                      <img
                        className="gray"
                        src={brandImage}
                        alt={`${brandName} Logo`}
                      />
                    </Link>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
}

export default BrandOne;
